import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import type { CancellationCondition } from "../../admin/dictionaries/cancellationConditionsApi";
import { useCancellationConditions } from "../../admin/dictionaries/useCancellationConditions";
import { Select } from "../../components/select/Select";

interface CancellationConditionInfoDialogProps {
  readonly condition: Pick<CancellationCondition, "shortName" | "text">;
}

const CancellationConditionInfoDialog: FC<CancellationConditionInfoDialogProps> = ({ condition }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Pokaż warunki anulacji">
        <IconButton
          size="small"
          sx={{ color: "text.secondary" }}
          aria-label="Pokaż warunki anulacji"
          onClick={() => setOpen(true)}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        aria-labelledby="cancellation-condition-dialog-title"
        onClose={() => setOpen(false)}
      >
        <DialogTitle id="cancellation-condition-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlinedIcon color="primary" fontSize="small" />
          Warunki anulacji: {condition.shortName}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: "pre-line" }}>{condition.text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface Props {
  readonly value: string;
  /** Also called once with the default condition when no value is set yet. Pass a stable reference. */
  readonly onChange: (cancellationConditionId: string) => void;
}

const CancellationConditionSelect: FC<Props> = ({ value, onChange }) => {
  const { conditions } = useCancellationConditions();

  useEffect(() => {
    if (value) {
      return;
    }
    const defaultId = conditions.find((c) => c.default)?.id ?? conditions[0]?.id;
    if (defaultId) {
      onChange(defaultId);
    }
  }, [conditions, value, onChange]);

  const selectedCondition = conditions.find((c) => c.id === value);

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <Box sx={{ flex: 1 }}>
        <Select
          label="Warunki anulacji"
          fullWidth
          value={value}
          values={conditions.map((c) => ({ value: c.id, label: c.shortName }))}
          onSelect={onChange}
        />
      </Box>
      {selectedCondition && <CancellationConditionInfoDialog condition={selectedCondition} />}
    </Stack>
  );
};

export default CancellationConditionSelect;
